import * as React from 'react';

import { Camada, Celula, type CampoDeCor } from '@/componentes/celula';
import { SeloEstado, type TomSelo } from '@/componentes/selo-estado';
import { cn } from '@/lib/utils';

/* PainelVeredito — a resposta da tela, em um bloco.
 *
 * Campo mata por padrão: é o campo do que FICA e do que se decide, e a regra
 * do sistema é que ele apareça no MÁXIMO duas vezes por tela (o veredito e a
 * decisão). Se um terceiro bloco mata aparecer, nenhum dos três é o veredito.
 *
 * A ordem dos elementos é a ordem em que a pergunta é respondida:
 *   selo        o julgamento em uma palavra
 *   rótulo      de que grandeza estamos falando
 *   cifra       o número que responde
 *   de → para   o que muda, quando há uma comparação
 *   explicação  por que, em prosa
 *   medidor     a mesma resposta em objeto, para quem lê em imagem
 *   ações       o que fazer a respeito
 *
 * Nada aqui sabe o que é `recomendacao.estado`. A tela traduz o estado do
 * motor em tom e texto; este componente só desenha. */

export type PainelVereditoProps = {
  /** O julgamento. Sem selo o painel vira um cartão grande e perde a função. */
  selo?: { tom: TomSelo; texto: string } | undefined;
  rotulo: string;
  /**
   * O número que responde. Normalmente um `<Valor tamanho="display">`.
   *
   * OPCIONAL de propósito: nos estados em que o motor recusa opinar não existe
   * número que responda, e um travessão em corpo de display é a tela fingindo
   * resposta com 46px de altura. Sem cifra, o degrau não é desenhado e o
   * rótulo passa a encostar na explicação.
   */
  cifra?: React.ReactNode;
  /** Valor de partida, riscado. Só com `para`. */
  de?: React.ReactNode;
  /** Valor de chegada. */
  para?: React.ReactNode;
  /** Selo curto ao lado da comparação: a variação em percentual. */
  variacao?: React.ReactNode;
  /** O porquê, em prosa. Entra numa camada recuada dentro do painel. */
  explicacao?: React.ReactNode;
  /** O objeto: normalmente um `<CelulaNivel>`. */
  medidor?: React.ReactNode;
  acoes?: React.ReactNode;
  campo?: CampoDeCor | undefined;
  assenta?: boolean | undefined;
  atraso?: number | undefined;
  className?: string | undefined;
};

export function PainelVeredito({
  selo,
  rotulo,
  cifra,
  de,
  para,
  variacao,
  explicacao,
  medidor,
  acoes,
  campo = 'mata',
  assenta,
  atraso,
  className,
}: PainelVereditoProps) {
  return (
    <Celula
      campo={campo}
      respiro="folgado"
      como="section"
      {...(assenta === undefined ? {} : { assenta })}
      {...(atraso === undefined ? {} : { atraso })}
      className={className}
    >
      <div
        className={cn(
          'grid items-center gap-6',
          // O medidor só ganha coluna própria quando cabe inteiro. Abaixo de
          // 900px ele desce e centraliza, em vez de espremer a cifra.
          medidor ? '@coluna-larga:grid-cols-[1fr_272px] @coluna-larga:gap-8 @painel-largo:grid-cols-[1fr_320px]' : undefined,
        )}
      >
        <div className="min-w-0">
          {selo ? (
            <SeloEstado tom={selo.tom} className="mb-5">
              {selo.texto}
            </SeloEstado>
          ) : null}

          <span className={cn('rotulo', cifra === undefined ? 'mb-3' : 'mb-2')}>{rotulo}</span>
          {cifra === undefined ? null : (
            <div className="cifra text-display leading-none">{cifra}</div>
          )}

          {para ? (
            <div className="numero mt-4 flex flex-wrap items-baseline gap-3">
              {de ? (
                <span className="text-tinta-2 decoration-negativo text-sub line-through decoration-[1.5px]">
                  {de}
                </span>
              ) : null}
              <span aria-hidden="true" className="text-tinta-3">
                →
              </span>
              <span className="text-titulo font-semibold tracking-tight">{para}</span>
              {variacao ? (
                <SeloEstado tom="positivo" marcador={false}>
                  {variacao}
                </SeloEstado>
              ) : null}
            </div>
          ) : null}

          {explicacao ? (
            <Camada className="apoio mt-5 max-w-leitura">
              {explicacao}
            </Camada>
          ) : null}

          {acoes ? <div className="mt-5 flex flex-wrap items-center gap-2">{acoes}</div> : null}
        </div>

        {medidor ? <div className="justify-self-center">{medidor}</div> : null}
      </div>
    </Celula>
  );
}
